import moment from 'moment-timezone';

/**
 * Canonical date range covered by a timeframe dropdown option, for display only.
 *
 * Mirrors app/Helpers/Timeframe.php and the RTF-4257 ticket examples:
 *   - "to date" options (weekToDate / monthToDate) and last30 end on *today*
 *   - the week starts on Monday (ISO week), matching backend Carbon startOfWeek()
 *   - lifetime starts at the business's created_at (first activity) and ends today
 *
 * @param {string} id               today|yesterday|weekToDate|monthToDate|last30|lifetime|custom
 * @param {string} [timezone]       business timezone (e.g. "America/Chicago"); falls back to local guess
 * @param {string} [lifetimeStart]  ISO date string for the lifetime start (business created_at)
 * @returns {{start: moment.Moment, end: moment.Moment}|null}  null for custom / unknown / missing lifetime data
 */
export function getTimeframeRange(id, timezone, lifetimeStart) {
    const tz = timezone || moment.tz.guess();
    const now = moment().tz(tz);
    const startOfToday = now.clone().startOf('day');

    switch (id) {
        case 'today':
            return { start: startOfToday, end: startOfToday.clone() };
        case 'yesterday': {
            const yesterday = startOfToday.clone().subtract(1, 'day');
            return { start: yesterday, end: yesterday.clone() };
        }
        case 'weekToDate':
            return { start: now.clone().startOf('isoWeek'), end: startOfToday.clone() };
        case 'monthToDate':
            return { start: now.clone().startOf('month'), end: startOfToday.clone() };
        case 'last30':
            return { start: startOfToday.clone().subtract(30, 'days'), end: startOfToday.clone() };
        case 'lifetime':
            if (!lifetimeStart) {
                return null;
            }

            return { start: moment.tz(lifetimeStart, tz), end: startOfToday.clone() };
        default:
            return null; // custom + unknown ids carry no range
    }
}

/**
 * Bare date range covered by a timeframe option, without surrounding parentheses.
 *
 * Examples: "10/29/2025" for single-day options, "9/30/2025 – 10/29/2025" for ranges,
 * and "" for Custom or when the range can't be computed (e.g. Lifetime with no created_at).
 * Used for the light-gray range shown inside the dropdown options.
 *
 * @param {string} id
 * @param {string} [timezone]
 * @param {string} [lifetimeStart]
 * @returns {string}
 */
export function timeframeRangeText(id, timezone, lifetimeStart) {
    const range = getTimeframeRange(id, timezone, lifetimeStart);
    if (!range) {
        return '';
    }

    const format = 'M/D/YYYY';
    const start = range.start.format(format);
    const end = range.end.format(format);

    return start === end ? start : `${start} – ${end}`;
}

/**
 * Suffix showing the dates a timeframe option covers, parenthesized for the label
 * rendered to the RIGHT of the dropdown.
 *
 * Examples: " (10/29/2025)" for single-day options, " (9/30/2025 – 10/29/2025)" for ranges,
 * and "" for Custom or when the range can't be computed (e.g. Lifetime with no created_at).
 *
 * @param {string} id
 * @param {string} [timezone]
 * @param {string} [lifetimeStart]
 * @returns {string}
 */
export function timeframeRangeSuffix(id, timezone, lifetimeStart) {
    const text = timeframeRangeText(id, timezone, lifetimeStart);

    return text ? ` (${text})` : '';
}

/**
 * Earliest created_at across a list of businesses, used as the Lifetime start for
 * multi-business dropdowns (dashboards). Returns null when none have a created_at.
 *
 * @param {Array<{created_at?: string}>} businesses
 * @returns {string|null} ISO date string of the earliest created_at, or null
 */
export function earliestCreatedAt(businesses) {
    if (!Array.isArray(businesses)) {
        return null;
    }

    return businesses.reduce((earliest, business) => {
        const createdAt = business && business.created_at;
        if (!createdAt) {
            return earliest;
        }
        if (!earliest || moment(createdAt).isBefore(moment(earliest))) {
            return createdAt;
        }

        return earliest;
    }, null);
}
